import {
  ButtonAppearance,
  ButtonCustom,
  CustomTextInput,
  DisableFocusWrapper,
  TextAppearance,
  TextColor,
  TextCustom,
  TextInputAppearance,
  buttonPaddingGenerator,
  Text,
  Link,
  Richtext,
  LinkProps,
  createTagOverride,
} from '@vw-marketing/us-components';
import React, { useEffect, useState } from 'react';
import {
  InputContainer,
  InputZipWrapper,
  SubmitButtonWrapper,
  RichtextWrapper,
} from './styled';
import { AppConstants } from '../../utils/app-constants';
import { useTrackingManager } from '../../services/use-tracking-manager';

interface ErrorState {
  state: boolean;
  message?: string;
  vin?: string;
}

interface EmissionsInputProps {
  onSubmitHandler: (vin: string) => void;
  errorRequest: ErrorState;
}

const errorMessages = {
  invalidVin: AppConstants.invalidVinMessage,
  ioqError:
    'Letters O, I, or Q should not be used. Please check your VIN and try again.',
};

export const EmissionsInput: React.FC<EmissionsInputProps> = ({
  onSubmitHandler,
  errorRequest,
}) => {
  const [vin, setVin] = useState('');
  const trackingManager = useTrackingManager();
  const [errorState, setErrorState] = useState<ErrorState>({
    state: false,
    message: '',
  });
  const [validVin, setValidVin] = useState<boolean>(false);
  const vinMaxLength = 17;
  const ioqErrorRegex = /[iqo]/gi;

  const onChangeHandler = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;

    if (value.length <= vinMaxLength) {
      const newValue = value.toLocaleUpperCase();
      setVin(newValue);
    }
  };

  const onClickButton = () => {
    trackingManager.trackEmissionsGlobalEvent(
      'VWBasic_VINFormCTA_Click',
      {
        formType: 'otherform',
        formName: 'Emission Request',
        formVersion: '1',
        url: 'open results',
        name: 'Emission Request',
        vin,
      },
      undefined,
    );

    const ioqError = ioqErrorRegex.test(vin) && vin !== '';
    if (ioqError) {
      setErrorState({ state: true, message: errorMessages.ioqError });
      trackingManager.trackEmissionsGlobalEvent(
        'VWBasic_VINFormErrorMessage_Load',
        {
          formType: 'otherform',
          formVersion: '1',
          formName: 'Emission Request',
          message: 'Letters O, I, or Q should not be used',
          code: '400',
        },
        undefined,
      );
    } else {
      setErrorState({ state: false, message: '' });
      onSubmitHandler(vin);
    }
  };

  const onClickLink = (e: any) => {
    const elem = e.target;
    const domain = window.location.hostname;
    const href = elem.getAttribute('href');
    const target = elem.getAttribute('target');
    const value = elem.text;

    trackingManager.trackEmissionsGlobalEvent(
      'VWBasic_Link_Click',
      {
        url: `${domain}${href}`,
        name: value,
        newTab: target === '_blank' ? true : false,
      },
      undefined,
    );
  };

  const createErrorText = (message?: string) => {
    const tagOverrides = {
      span: createTagOverride(Text, {
        appearance: TextAppearance.copy100,
        color: TextColor.inherit,
      }),
      a: createTagOverride(Link, {
        isInline: true,
        target: '_blank',
        onClick: onClickLink,
      } as LinkProps),
    };

    return (
      message && (
        <RichtextWrapper>
          <DisableFocusWrapper>
            <Richtext markdown={message} overrides={tagOverrides} />
          </DisableFocusWrapper>
        </RichtextWrapper>
      )
    );
  };

  // If change VIN
  useEffect(() => {
    const invalidVin = ioqErrorRegex.test(vin);
    setErrorState({ state: invalidVin, message: errorMessages.ioqError });
    vinMaxLength === vin.length ? setValidVin(true) : setValidVin(false);
  }, [vin]);

  useEffect(() => {
    if (errorRequest.state) {
      setErrorState(errorRequest);
    } else {
      setErrorState({ state: false, message: '' });
    }
  }, [errorRequest]);

  return (
    <InputZipWrapper>
      <InputContainer>
        <CustomTextInput
          name={'vin'}
          appearance={
            errorState.state
              ? TextInputAppearance.Error
              : TextInputAppearance.Default
          }
          placeholder={'Enter your 17-character VIN'}
          value={vin}
          onChange={onChangeHandler}
          errorText={createErrorText(errorState.message)}
          useCustomZipText={true}
          onKeyPress={() => (validVin ? onClickButton() : null)}
        />
        <SubmitButtonWrapper>
          <DisableFocusWrapper>
            <ButtonCustom
              appearance={ButtonAppearance.Primary}
              onClick={onClickButton}
              padding={buttonPaddingGenerator(0, 52, 52, 52)}
              disabled={!validVin}
            >
              <TextCustom
                appearance={TextAppearance.label100}
                style={{ color: TextColor.inherit }}
              >
                Submit
              </TextCustom>
            </ButtonCustom>
          </DisableFocusWrapper>
        </SubmitButtonWrapper>
      </InputContainer>
    </InputZipWrapper>
  );
};
